import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, ArrowRight, Award, Clock, Users, Star, ChevronLeft, ChevronRight, MessageCircle, TrendingUp, Phone, Mail, Search, FileText, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useContactPopup } from '@/hooks/useContactPopup';
import { getGeoLocation, submitLead } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { countryCodes } from '@/data/countryCodes';

export const LandingPage = () => {
  const { openPopup } = useContactPopup();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+44',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const result = await getGeoLocation();
      if (result.success) {
        setGeoData(result.data);
      }
    };
    fetchGeoLocation();
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: digitsOnly });
    
    if (digitsOnly && !validatePhone(digitsOnly)) {
      setErrors({ ...errors, phone: 'Phone number must be exactly 10 digits' });
    } else {
      setErrors({ ...errors, phone: '' });
    }
  };

  const handleEmailChange = (value) => {
    setFormData({ ...formData, email: value });
    
    if (value && !validateEmail(value)) {
      setErrors({ ...errors, email: 'Please enter a valid email address (e.g., user@example.com)' });
    } else {
      setErrors({ ...errors, email: '' });
    }
  };

  // Function to get visible testimonials based on viewport
  const getVisibleTestimonials = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const testimonialsPerView = isMobile ? 1 : 3;
    
    const visible = [];
    for (let i = 0; i < testimonialsPerView; i++) {
      const index = (currentTestimonial + i) % testimonials.length;
      visible.push({ ...testimonials[index], originalIndex: index });
    }
    return visible;
  };

  const nextTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev + step) % testimonials.length);
  };

  const prevTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev - step + testimonials.length) % testimonials.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    const newErrors = {};
    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await submitLead(formData, geoData);
      
      if (result.success) {
        toast.success(result.message || 'Thank you! We\'ll contact you shortly.');
        setFormData({ name: '', email: '', countryCode: '+44', phone: '', message: '' });
        setErrors({ email: '', phone: '' });
        navigate(`/application/main?customerid=${result.customerId}`);
      } else {
        toast.error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChatNow = () => {
    try {
      // Open Zendesk chat widget - use webWidget API
      if (typeof window !== 'undefined') {
        if (window.zE) {
          // Try both methods for compatibility
          try {
            window.zE('messenger', 'open');
          } catch (e) {
            // Fallback to webWidget API
            if (window.zE.activate) {
              window.zE.activate();
            }
          }
        } else {
          // Widget not loaded yet - retry after delay
          setTimeout(() => {
            if (window.zE) {
              window.zE('messenger', 'open');
            } else {
              console.log('Please use the chat widget in the bottom-right corner');
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error opening chat:', error);
    }
  };

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Founder, TechFlow Solutions',
      content: 'Trademark Assist made registration effortless. Professional handling from search to approval. Certificate received in just 5 months.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=128&h=128&fit=crop&q=80'
    },
    {
      name: 'James Robertson',
      role: 'Creative Director, Pixel Studios',
      content: 'Excellent service for copyright registration. Clear explanations and transparent pricing. They\'re our go-to for all IP matters.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=128&h=128&fit=crop&q=80'
    },
    {
      name: 'Priya Sharma',
      role: 'Marketing Director, GreenLeaf',
      content: 'Comprehensive search uncovered a conflict we missed. Saved us from legal issues and helped us choose a stronger brand name.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=128&h=128&fit=crop&q=80'
    },
    {
      name: 'Michael Chen',
      role: 'Co-Founder, UrbanBuild Ltd',
      content: 'Company formation made seamless. All paperwork handled efficiently with incorporation certificate delivered within 24 hours.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=128&h=128&fit=crop&q=80'
    },
    {
      name: 'Emma Thompson',
      role: 'E-commerce Manager, Heritage Goods',
      content: 'Perfect guidance for Amazon Brand Registry. Our sales increased 40% in three months thanks to added trust and protection.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=128&h=128&fit=crop&q=80'
    }
  ];

  const pricingPackages = [
    {
      name: 'Trade Mark Watch',
      price: '£350',
      period: 'Yearly',
      description: 'Ongoing protection and monitoring',
      features: [
        'Continuous Trademark Monitoring',
        'Alert for Similar Applications',
        'Annual Renewal Reminders',
        'Protection Status Updates',
        'Email Support'
      ]
    },
    {
      name: 'Basic Trade Mark Registration',
      price: 'from £299',
      govtFee: '+ £170 IPO Govt Fee',
      description: 'Perfect for startups and small businesses',
      features: [
        'Single Class Registration',
        'Basic Trademark Search',
        'Application Preparation & Filing',
        'Standard Processing',
        'Email Support'
      ]
    },
    {
      name: 'Premium TRADE MARK REGISTRATION',
      price: 'from £499',
      govtFee: '+ £170 IPO Govt Fee',
      popular: true,
      description: 'Most popular for growing businesses',
      features: [
        'Single Class Registration',
        'Comprehensive Trademark Search',
        'Priority Application Handling',
        'Office Action Response',
        'Free 30-min Consultation',
        'Dedicated Phone Support'
      ]
    }
  ];

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      nextTestimonial();
    }, 5000); // Smooth continuous slide every 5 seconds

    return () => clearInterval(testimonialInterval);
  }, []); // Remove dependency to prevent timer restart

  return (
    <div className="min-h-screen bg-background">
      {/* Custom Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_trademark-assist-1/artifacts/xoi44mz8_logo.png" 
              alt="Trademark Assist Logo" 
              className="h-12 w-auto"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              onClick={openPopup}
              className="bg-gradient-to-r from-[#012169] to-[#C8102E] hover:from-[#011354] hover:to-[#a80d25] text-white shadow-lg"
            >
              Register My Trademark
            </Button>
            <Button 
              onClick={handleChatNow}
              variant="outline"
              className="border-2 border-primary hover:bg-primary hover:text-primary-foreground"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Chat Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section with Form */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Animated UK Background with Famous Landmark */}
        <div className="absolute inset-0 z-0">
          {/* UK Landmark Background - Big Ben */}
          <img
            src="https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&h=1080&fit=crop"
            alt="London Big Ben UK Landmark"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          {/* Animated UK Flag GIF - Larger and More Visible */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <img
              src="https://www.3dflagsplus.com/gifs/United_Kingdom_240x240.gif"
              alt="UK Flag Animation"
              className="w-64 h-64 opacity-25"
            />
          </div>
          {/* Gradient Overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-background/85 via-background/80 to-secondary/70"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Right: Registration Form - Shows First on Mobile */}
            <motion.div
              id="lp-form"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <Card className="p-8 border-2 border-primary/20 shadow-2xl bg-white">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-foreground mb-2">Get Started in 60 Seconds</h2>
                  <p className="text-lg text-muted-foreground">Expert consultation within 24 hours • No obligation</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="lp-name">Full Name *</Label>
                    <Input
                      id="lp-name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lp-email">Email *</Label>
                    <Input
                      id="lp-email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      required
                      className={errors.email ? 'border-red-500' : ''}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500">{errors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lp-phone">Contact Number *</Label>
                    <div className="flex gap-2">
                      <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
                        <SelectTrigger className="w-[110px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map((item) => (
                            <SelectItem key={item.code} value={item.code}>
                              {item.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex-1 space-y-2">
                        <Input
                          id="lp-phone"
                          type="tel"
                          placeholder="1234567890 (10 digits)"
                          value={formData.phone}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          required
                          maxLength={10}
                          className={errors.phone ? 'border-red-500' : ''}
                        />
                        {errors.phone && (
                          <p className="text-sm text-red-500">{errors.phone}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lp-message">Description</Label>
                    <Textarea
                      id="lp-message"
                      placeholder="Tell us about your trademark needs..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#012169] to-[#C8102E] hover:from-[#011354] hover:to-[#a80d25] text-white text-lg py-6 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Register My Trademark Now'}
                  </Button>
                </form>
              </Card>
            </motion.div>

            {/* Left: Content - Shows Second on Mobile */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              {/* Marketing Upper Line */}
              <p className="text-lg font-bold text-primary mb-3 uppercase tracking-wide">
                Secure Your Business Identity Before Competitors Do Right Now
              </p>
              
              {/* Main Heading */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Protect Your UK Brand Today
              </h1>
              
              {/* Paragraph */}
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Register your trademark with the UK's most trusted service. Our expert team ensures fast, professional registration with a 98% approval rate. Protect your brand name, logo, and business identity from competitors. Join over 10,000 satisfied UK businesses who secured their trademarks with us.
              </p>
              
              {/* Special Offer Highlight */}
              <div className="bg-gradient-to-r from-[#012169] to-[#C8102E] text-white p-6 rounded-xl mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <Badge className="bg-white text-primary">Limited Time Offer</Badge>
                </div>
                <p className="text-3xl font-bold mb-2">£350 Yearly</p>
                <p className="text-lg opacity-90">Complete UK Trademark Registration Package</p>
                <p className="text-sm opacity-75 mt-2">Includes comprehensive search, filing, and monitoring</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why You Need This Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            {/* Left: Image - Shows First on Mobile */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-full min-h-[300px] lg:min-h-[500px] order-1"
            >
              <img
                src="https://images.pexels.com/photos/7715105/pexels-photo-7715105.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Professional Trademark Protection Services"
                className="w-full h-full object-cover rounded-2xl shadow-2xl"
              />
            </motion.div>

            {/* Right: Content - Shows Second on Mobile */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col justify-center order-2"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Why Your Business Needs Trademark Protection
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Without a registered trademark, your brand name, logo, and business identity are vulnerable to copycats and competitors. A UK trademark gives you exclusive legal rights to your brand across England, Scotland, Wales, and Northern Ireland. It prevents others from using similar names that could confuse your customers, protects your reputation, adds significant value to your business, and enables you to take swift legal action against infringers. Don't wait until a competitor registers a similar mark—protect your investment and secure your brand's future today.
              </p>
              <Button 
                onClick={openPopup}
                size="lg"
                className="bg-gradient-to-r from-[#012169] to-[#C8102E] hover:from-[#011354] hover:to-[#a80d25] text-white shadow-lg"
              >
                Protect My Brand Now
                <Shield className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#012169]/5 to-[#C8102E]/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Our Simple 3-Step Trademark Registration Process
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              We handle everything from start to finish, making trademark registration effortless for you. Our proven process ensures maximum approval chances and complete legal protection for your brand.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <motion.div
              whileHover={{ scale: 1.05, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-8 border-2 border-border hover:border-primary hover:shadow-2xl transition-all duration-300 group h-full hover:bg-gradient-to-br hover:from-[#012169]/5 hover:to-[#C8102E]/5">
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-br from-[#012169] to-[#C8102E] rounded-full flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform duration-300"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <FileText className="w-8 h-8" />
                </motion.div>
                <h3 className="text-2xl font-bold text-foreground mb-3 text-center group-hover:text-primary transition-colors duration-300">1. Information Collection</h3>
                <p className="text-muted-foreground text-center leading-relaxed">
                  Submit your trademark details through our simple form. Our experts review your information and provide personalized guidance within 24 hours.
                </p>
              </Card>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              whileHover={{ scale: 1.05, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-8 border-2 border-border hover:border-primary hover:shadow-2xl transition-all duration-300 group h-full hover:bg-gradient-to-br hover:from-[#012169]/5 hover:to-[#C8102E]/5">
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-br from-[#012169] to-[#C8102E] rounded-full flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform duration-300"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <Search className="w-8 h-8" />
                </motion.div>
                <h3 className="text-2xl font-bold text-foreground mb-3 text-center group-hover:text-primary transition-colors duration-300">2. Research & Search Report</h3>
                <p className="text-muted-foreground text-center leading-relaxed">
                  We conduct comprehensive searches across UK IPO, Companies House, and domain databases to ensure your mark is unique and registrable.
                </p>
              </Card>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              whileHover={{ scale: 1.05, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-8 border-2 border-border hover:border-primary hover:shadow-2xl transition-all duration-300 group h-full hover:bg-gradient-to-br hover:from-[#012169]/5 hover:to-[#C8102E]/5">
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-br from-[#012169] to-[#C8102E] rounded-full flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform duration-300"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <Send className="w-8 h-8" />
                </motion.div>
                <h3 className="text-2xl font-bold text-foreground mb-3 text-center group-hover:text-primary transition-colors duration-300">3. Application & Submission</h3>
                <p className="text-muted-foreground text-center leading-relaxed">
                  Our trademark attorneys prepare and file your application with the UK IPO, handling all legal requirements and monitoring until registration.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Transparent Pricing - No Hidden Fees
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Choose the package that fits your business needs. All packages include expert handling, comprehensive searches, and professional application preparation with no surprise costs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricingPackages.map((pkg, index) => (
              <Card 
                key={index}
                className={`p-8 border-2 transition-all hover:shadow-2xl flex flex-col ${
                  pkg.popular 
                    ? 'border-primary shadow-xl relative' 
                    : 'border-border'
                }`}
              >
                {pkg.popular && (
                  <Badge className="absolute top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#012169] to-[#C8102E] text-white px-4 py-1">
                    Most Popular
                  </Badge>
                )}
                <div className={`text-center mb-6 ${pkg.popular ? 'mt-8' : ''}`}>
                  <h3 className="text-2xl font-bold text-foreground mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-primary mb-1">{pkg.price}</div>
                  {pkg.period && (
                    <div className="text-lg font-semibold text-muted-foreground mb-1">{pkg.period}</div>
                  )}
                  {pkg.govtFee && (
                    <div className="text-sm font-medium text-[#C8102E] mb-2">{pkg.govtFee}</div>
                  )}
                  <p className="text-sm text-muted-foreground">{pkg.description}</p>
                </div>
                <ul className="space-y-3 mb-8 flex-grow">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={openPopup}
                  className={pkg.popular 
                    ? 'w-full bg-gradient-to-r from-[#012169] to-[#C8102E] hover:from-[#011354] hover:to-[#a80d25] text-white' 
                    : 'w-full border-2 border-primary hover:bg-primary hover:text-primary-foreground'
                  }
                  variant={pkg.popular ? 'default' : 'outline'}
                >
                  Register My Trademark
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-background to-accent/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Right: Image - Shows First on Mobile */}
            <div className="relative order-1 lg:order-2">
              <img 
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop&q=80"
                alt="Trademark Registration Success"
                className="rounded-2xl shadow-2xl w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#012169]/30 to-transparent rounded-2xl"></div>
            </div>

            {/* Left: Content - Shows Second on Mobile */}
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Why 10,000+ UK Businesses Choose Trademark Assist
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Since 2009, we've been the UK's most trusted trademark registration service. Our team of experienced trademark attorneys brings over 15 years of combined expertise, achieving an exceptional 98% approval rate on applications. We don't just file paperwork—we provide strategic guidance tailored to your business needs. Our comprehensive searches identify potential conflicts before filing, saving you time and money. With transparent pricing, no hidden fees, and dedicated support throughout the process, we transform complex legal procedures into straightforward solutions. Join thousands of satisfied clients who secured their trademarks with confidence through our proven expertise.
              </p>
              <Button 
                onClick={openPopup}
                size="lg"
                className="bg-gradient-to-r from-[#012169] to-[#C8102E] hover:from-[#011354] hover:to-[#a80d25] text-white shadow-lg"
              >
                Start Your Registration
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Company Stats Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Trusted by UK's Leading Businesses
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center border-2 border-border hover:border-primary/50 hover:shadow-xl transition-all">
              <div className="text-6xl font-bold text-primary mb-4">98%</div>
              <h3 className="text-2xl font-bold text-foreground mb-3">Success Rate</h3>
              <p className="text-muted-foreground">
                Industry-leading approval rate backed by expert handling and comprehensive searches, ensuring your trademark application succeeds.
              </p>
            </Card>

            <Card className="p-8 text-center border-2 border-border hover:border-primary/50 hover:shadow-xl transition-all">
              <div className="text-6xl font-bold text-primary mb-4">10,000+</div>
              <h3 className="text-2xl font-bold text-foreground mb-3">Trademarks Registered</h3>
              <p className="text-muted-foreground">
                Successfully protecting UK businesses since 2009 across every industry from startups to established enterprises.
              </p>
            </Card>

            <Card className="p-8 text-center border-2 border-border hover:border-primary/50 hover:shadow-xl transition-all">
              <div className="text-6xl font-bold text-primary mb-4">24hrs</div>
              <h3 className="text-2xl font-bold text-foreground mb-3">Response Time</h3>
              <p className="text-muted-foreground">
                Fast, professional service with expert consultation within 24 hours of your initial inquiry. We value your time.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section with Background Image */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1920&h=600&fit=crop&q=80"
            alt="Business Success"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#012169]/95 to-[#C8102E]/95"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10 flex flex-col items-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Don't Let Competitors Steal Your Brand Identity
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed text-center max-w-3xl">
            Every day without trademark protection puts your business at risk. Secure your brand name, logo, and reputation before it's too late. Join over 10,000 UK businesses who protected their trademarks with our expert service. Start your registration today and gain exclusive rights to your brand.
          </p>
          <Button 
            onClick={openPopup}
            size="lg"
            className="bg-white text-primary hover:bg-gray-100 shadow-xl w-full sm:w-auto px-4 sm:px-6 text-sm sm:text-base whitespace-normal sm:whitespace-nowrap mx-auto"
          >
            <span className="inline sm:hidden">Register Now - £350/Year</span>
            <span className="hidden sm:inline">Register My Trademark Now - £350/Year</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 flex-shrink-0" />
          </Button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#012169]/10 via-background to-[#C8102E]/10 relative overflow-hidden">
        {/* UK Theme Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#012169] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#C8102E] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-gradient-to-r from-[#012169] to-[#C8102E] text-white">Client Success Stories</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                What Our Clients Say About Us
              </h2>
              <p className="text-lg text-muted-foreground">Real experiences from businesses we've helped protect</p>
            </motion.div>
          </div>

          <div className="relative">
            {/* Left Arrow */}
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-20 bg-gradient-to-r from-[#012169] to-[#C8102E] text-white rounded-full p-3 shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300"
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Right Arrow */}
            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-20 bg-gradient-to-r from-[#012169] to-[#C8102E] text-white rounded-full p-3 shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300"
              aria-label="Next testimonials"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Testimonials Grid with Center Focus */}
            <div className="overflow-hidden px-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ 
                    duration: 0.5,
                    ease: "easeInOut"
                  }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center"
                >
                  {getVisibleTestimonials().map((testimonial, index) => {
                    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
                    const isCenterCard = isMobile ? index === 0 : index === 1;
                    
                    return (
                    <div 
                      key={`${testimonial.originalIndex}-${index}`}
                      className={`transition-all duration-500 ${
                        isCenterCard ? 'md:z-10 scale-105' : 'md:scale-95'
                      }`}
                      style={{
                        opacity: isMobile ? 1 : (isCenterCard ? 1 : 0.6),
                        filter: isMobile ? 'blur(0px)' : (isCenterCard ? 'blur(0px)' : 'blur(3px)')
                      }}
                    >
                      <Card className={`p-8 h-full flex flex-col border-2 transition-all duration-500 relative ${
                        isCenterCard
                          ? 'border-[#012169] bg-white shadow-2xl'
                          : 'border-border bg-white/80'
                      }`}>
                        {/* Profile Image */}
                        <div className="flex items-center gap-4 mb-6">
                          <div 
                            className={`relative w-16 h-16 rounded-full overflow-hidden border-4 ${
                              isCenterCard 
                                ? 'border-[#012169]' 
                                : 'border-gray-300'
                            }`}
                          >
                            <img 
                              src={testimonial.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=012169&color=fff&size=128`}
                              alt={testimonial.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="font-bold text-foreground text-lg">{testimonial.name}</p>
                            <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                          </div>
                        </div>

                        {/* Star Rating */}
                        <div className="flex items-center gap-1 mb-4">
                          {Array.from({ length: 5 }).map((_, star) => {
                            const rating = testimonial.rating;
                            const isFullStar = star < Math.floor(rating);
                            const isHalfStar = star === Math.floor(rating) && rating % 1 !== 0;
                            
                            return (
                              <div 
                                key={star} 
                                className="relative w-5 h-5"
                              >
                                {isHalfStar ? (
                                  <>
                                    <Star className="w-5 h-5 text-gray-300 absolute inset-0" fill="currentColor" />
                                    <div className="overflow-hidden absolute w-2.5 h-5">
                                      <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                                    </div>
                                  </>
                                ) : (
                                  <Star 
                                    className={`w-5 h-5 ${isFullStar ? 'text-yellow-400' : 'text-gray-300'}`} 
                                    fill="currentColor" 
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Testimonial Content */}
                        <p 
                          className="text-base text-foreground mb-6 leading-relaxed italic flex-1"
                        >
                          &quot;{testimonial.content}&quot;
                        </p>
                        
                        {/* UK Theme Accent */}
                        {isCenterCard && (
                          <div 
                            className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#012169] to-[#C8102E] opacity-10 rounded-bl-full"
                          ></div>
                        )}
                      </Card>
                    </div>
                  )})}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 mt-12">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index * 3)}
                  className={`h-3 rounded-full transition-all duration-500 ease-out ${
                    Math.floor(currentTestimonial / 3) === index
                      ? 'bg-gradient-to-r from-[#012169] to-[#C8102E] w-12 shadow-lg' 
                      : 'bg-border w-3 hover:bg-primary/50 hover:w-6'
                  }`}
                  aria-label={`View testimonial group ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border-2 border-border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                How long does UK trademark registration take?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                The entire UK trademark registration process typically takes 4-6 months from application to final registration. This includes the initial examination period (4-6 weeks), publication period (2 months), and final processing. We monitor your application throughout and handle any office actions to ensure smooth progression.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border-2 border-border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                What's included in the comprehensive trademark search?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Our comprehensive search examines UK IPO registered trademarks, pending applications, Companies House business names, domain name registrations, and common law marks. We analyze phonetic similarities, visual resemblances, and conceptual conflicts across all relevant trademark classes. You receive a detailed report with risk assessment and strategic recommendations before filing.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border-2 border-border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                Can I register multiple classes with one trademark?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Yes, you can register your trademark across multiple classes of goods and services. The UK uses the Nice Classification system with 45 classes. Each additional class incurs extra government fees. Our Premium Package includes registration in up to 3 classes. We help you identify the most appropriate classes for your business needs during consultation.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4" className="border-2 border-border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                What happens if my trademark application is rejected?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Our 98% success rate means rejections are rare. If the UK IPO raises objections, we respond to all office actions as part of our service (included in Professional and Premium packages). We analyze the examiner's concerns, prepare legal arguments, and amend the application if necessary. Most objections are successfully overcome through proper response strategies.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-5" className="border-2 border-border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold text-left">
                Do I need a trademark for my online business or e-commerce store?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Absolutely. Online businesses are particularly vulnerable to brand theft and copycats. A registered trademark protects your business name, logo, and brand across all channels including your website, social media, and marketplace platforms like Amazon. It's also required for Amazon Brand Registry, which provides enhanced protection and marketing tools. Without trademark registration, competitors can legally use similar names and confuse your customers.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="py-8 px-4 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm opacity-80">© 2026 Trademark Assist. All rights reserved.</p>
            </div>
            <div className="flex gap-6 text-sm">
              <a href="/privacy-policy" className="opacity-80 hover:opacity-100 transition-opacity">Privacy Policy</a>
              <a href="/terms-of-service" className="opacity-80 hover:opacity-100 transition-opacity">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
